// src/components/helpdesk/PublicReportsMap.js
// --- UPDATE (Phone AI Helpdesk) map of phone/public reports, hot-spot clusters, and the drainage
// wells / catch basins (with live water level) around flood reports. Plain Leaflet (already a dependency).
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, HStack, Select, Switch, FormControl, FormLabel, Text, Badge, Spinner, useColorModeValue, Wrap, WrapItem } from '@chakra-ui/react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import phoneHelpdeskApi, { CATEGORY_META, STAGE_LABEL } from '../../services/phoneHelpdeskApi';

const SEV_RADIUS = { low: 6, medium: 7, high: 9, critical: 11 };
const GROUP_COLOR = { road: '#dd6b20', flood: '#3182ce', other: '#805ad5' };
const LEVEL_COLOR = { HIGH: '#e53e3e', MEDIUM: '#d69e2e', LOW: '#38a169', EMPTY: '#718096' };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

export default function PublicReportsMap({ height = '520px', onSelectReport, refreshKey = 0, focus = null }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const [group, setGroup] = useState('');
  const [openOnly, setOpenOnly] = useState(true);
  const [drains, setDrains] = useState(true);
  const [days, setDays] = useState(14);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({ report: 0, cluster: 0, drain: 0 });
  const border = useColorModeValue('gray.200', 'whiteAlpha.300');
  const onSelRef = useRef(onSelectReport);
  const focusRef = useRef(focus);
  useEffect(() => { onSelRef.current = onSelectReport; focusRef.current = focus; }, [onSelectReport, focus]);

  useEffect(() => {
    if (!elRef.current || mapRef.current) return undefined;
    const map = L.map(elRef.current, { zoomControl: true, preferCanvas: true }).setView([25.04, -77.35], 12); // New Providence
    L.tileLayer(process.env.REACT_APP_MAP_TILES || 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '&copy; OpenStreetMap' }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  const load = useCallback(async () => {
    if (!layerRef.current) return;
    setLoading(true);
    try {
      const fc = await phoneHelpdeskApi.map({ days, group: group || undefined, open: openOnly ? 1 : undefined, drains: drains ? 1 : undefined });
      const lg = layerRef.current;
      lg.clearLayers();
      const c = { report: 0, cluster: 0, drain: 0 };
      const bounds = [];
      for (const f of fc?.features || []) {
        const [lng, lat] = f.geometry.coordinates;
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
        const p = f.properties;
        c[p.kind] = (c[p.kind] || 0) + 1;
        if (p.kind === 'cluster') {
          const col = GROUP_COLOR[p.category_group] || GROUP_COLOR.other;
          L.circle([lat, lng], { radius: Math.max(40, p.radius_m || 75), color: col, weight: 1, fillColor: col, fillOpacity: Math.min(0.45, 0.12 + 0.06 * (p.unique_callers || 1)) })
            .bindTooltip(`<b>Hot spot</b> · ${p.unique_callers} caller(s), ${p.report_count} report(s)<br/>${esc(p.label || '')}`)
            .addTo(lg);
        } else if (p.kind === 'drain') {
          L.circleMarker([lat, lng], { radius: 5, color: '#1a365d', weight: 1, fillColor: LEVEL_COLOR[p.level] || '#a0aec0', fillOpacity: 0.9 })
            .bindTooltip(`<b>${esc(p.type === 'catch_basin' ? 'Catch basin' : p.type === 'pump' ? 'Pump' : 'Drainage well')}</b><br/>${esc(p.name)}<br/>Level: ${esc(p.level || 'no sensor')}${p.elevation != null ? `<br/>Elevation: ${esc(p.elevation)} m` : ''}`)
            .addTo(lg);
        } else {
          bounds.push([lat, lng]);
          const meta = CATEGORY_META[p.category] || {};
          const col = GROUP_COLOR[meta.group || p.group] || GROUP_COLOR.other;
          const m = L.circleMarker([lat, lng], {
            radius: SEV_RADIUS[p.severity] || 7, color: p.hazard_to_life ? '#c53030' : '#ffffff', weight: p.hazard_to_life ? 3 : 1.5,
            fillColor: col, fillOpacity: p.verification_status === 'confirmed_false' ? 0.25 : 0.9,
          });
          m.bindTooltip(`<b>${esc(meta.label || p.category)}</b> · ${esc(p.severity)}<br/>${esc(p.ticket_number)} · ${esc(STAGE_LABEL[p.lifecycle_stage] || p.lifecycle_stage)}<br/>${esc(p.location_text || '')}`);
          m.on('click', () => onSelRef.current && onSelRef.current(p));
          m.addTo(lg);
        }
      }
      setCounts(c);
      if (!focusRef.current && bounds.length && mapRef.current) mapRef.current.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
    } catch (_) {
      setCounts({ report: 0, cluster: 0, drain: 0 });
    } finally {
      setLoading(false);
    }
  }, [days, group, openOnly, drains]);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    if (focus && mapRef.current && Number.isFinite(Number(focus.latitude))) mapRef.current.setView([Number(focus.latitude), Number(focus.longitude)], 17);
  }, [focus]);

  return (
    <Box borderWidth="1px" borderColor={border} borderRadius="xl" overflow="hidden">
      <Wrap spacing={3} p={3} align="center">
        <WrapItem><Select size="sm" value={group} onChange={(e) => setGroup(e.target.value)} w="150px">
          <option value="">All reports</option><option value="road">Road reports</option><option value="flood">Flooding / drains</option>
        </Select></WrapItem>
        <WrapItem><Select size="sm" value={days} onChange={(e) => setDays(Number(e.target.value))} w="120px">
          <option value={1}>24 h</option><option value={7}>7 days</option><option value={14}>14 days</option><option value={30}>30 days</option><option value={90}>90 days</option>
        </Select></WrapItem>
        <WrapItem><FormControl display="flex" alignItems="center"><FormLabel mb={0} fontSize="sm">Open only</FormLabel><Switch size="sm" isChecked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} /></FormControl></WrapItem>
        <WrapItem><FormControl display="flex" alignItems="center"><FormLabel mb={0} fontSize="sm">Drains near floods</FormLabel><Switch size="sm" isChecked={drains} onChange={(e) => setDrains(e.target.checked)} /></FormControl></WrapItem>
        <WrapItem>{loading ? <Spinner size="sm" /> : (
          <HStack spacing={2}>
            <Badge colorScheme="orange">{counts.report || 0} reports</Badge>
            <Badge colorScheme="purple">{counts.cluster || 0} hot spots</Badge>
            {drains && <Badge colorScheme="blue">{counts.drain || 0} drains</Badge>}
          </HStack>
        )}</WrapItem>
        <WrapItem><HStack spacing={3} fontSize="xs">
          <HStack spacing={1}><Box w="10px" h="10px" borderRadius="full" bg={GROUP_COLOR.road} /><Text>Road</Text></HStack>
          <HStack spacing={1}><Box w="10px" h="10px" borderRadius="full" bg={GROUP_COLOR.flood} /><Text>Flood</Text></HStack>
          <HStack spacing={1}><Box w="10px" h="10px" borderRadius="full" bg={LEVEL_COLOR.HIGH} /><Text>Drain HIGH</Text></HStack>
          <HStack spacing={1}><Box w="10px" h="10px" borderRadius="full" bg={LEVEL_COLOR.LOW} /><Text>Drain LOW</Text></HStack>
        </HStack></WrapItem>
      </Wrap>
      <Box ref={elRef} h={height} w="100%" />
    </Box>
  );
}
// --- END UPDATE
